import { useState } from 'react';
import tw from 'twin.macro';

import { PlusIcon } from '@heroicons/react/24/outline';

import { Button } from '@/components/base';
import {
  useCreateJobPositionMutation,
  useDeleteJobPositionMutation,
  useGetJobPositionsQuery,
  useUpdateJobPositionMutation,
} from '@/domains/position';
import { GetJobPositionsQuery, Roles } from '@/libs/graphql';
import { useBackofficeContext } from '@/providers/Backoffice';

import DeletePositionModal from './DeletePositionModal';
import PositionFormModal from './PositionFormModal';
import PositionsTable from './PositionsTable';

type JobPosition = GetJobPositionsQuery['getJobPositions'][number];

type ModalState =
  | { type: 'create' }
  | { type: 'edit'; position: JobPosition }
  | { type: 'delete'; position: JobPosition }
  | null;

const PositionsTab = () => {
  const { user } = useBackofficeContext();
  const isViewOnly = user?.roles?.includes(Roles.ViewOnly) ?? false;
  const [modalState, setModalState] = useState<ModalState>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data, loading: listLoading, refetch } = useGetJobPositionsQuery({
    fetchPolicy: 'network-only',
  });

  const [createJobPosition, { loading: createLoading }] = useCreateJobPositionMutation({
    onCompleted: () => {
      setModalState(null);
      refetch();
    },
  });

  const [updateJobPosition, { loading: updateLoading }] = useUpdateJobPositionMutation({
    onCompleted: () => {
      setModalState(null);
      refetch();
    },
  });

  const [deleteJobPosition, { loading: deleteLoading }] = useDeleteJobPositionMutation({
    onCompleted: (result) => {
      const res = result.deleteJobPosition;
      if (res.__typename === 'DeleteJobPositionInputError') {
        // e.g. the position is still used by an unfinished interview session.
        setDeleteError(res.message);
      } else {
        setModalState(null);
        setDeleteError(null);
        refetch();
      }
    },
  });

  const positions = data?.getJobPositions ?? [];

  const handleFormSubmit = (values: { title: string; jobDescriptions: string }) => {
    if (modalState?.type === 'create') {
      createJobPosition({ variables: { input: values } });
    } else if (modalState?.type === 'edit') {
      updateJobPosition({
        variables: { input: { id: modalState.position.id, ...values } },
      });
    }
  };

  const handleDeleteConfirm = () => {
    if (modalState?.type === 'delete') {
      deleteJobPosition({ variables: { id: modalState.position.id } });
    }
  };

  return (
    <div tw="flex flex-col gap-4">
      <div tw="flex items-center justify-between">
        <p tw="text-sm text-gray-500">
          {positions.length > 0
            ? `${positions.length} ตำแหน่งงาน`
            : 'ยังไม่มีตำแหน่งงาน'}
        </p>
        {!isViewOnly && (
          <Button
            isSmall
            onClick={() => setModalState({ type: 'create' })}
            tw="flex items-center gap-1 px-4 py-2"
          >
            <PlusIcon tw="w-4 h-4" />
            เพิ่มตำแหน่งงาน
          </Button>
        )}
      </div>

      {listLoading ? (
        <div tw="py-8 text-center text-gray-400 text-sm">กำลังโหลด...</div>
      ) : (
        <PositionsTable
          positions={positions}
          isViewOnly={isViewOnly}
          onEdit={(position) => setModalState({ type: 'edit', position })}
          onDelete={(position) => {
            setDeleteError(null);
            setModalState({ type: 'delete', position });
          }}
        />
      )}

      {modalState?.type === 'create' && (
        <PositionFormModal
          mode="create"
          loading={createLoading}
          onSubmit={handleFormSubmit}
          onClose={() => setModalState(null)}
        />
      )}

      {modalState?.type === 'edit' && (
        <PositionFormModal
          mode="edit"
          position={modalState.position}
          loading={updateLoading}
          onSubmit={handleFormSubmit}
          onClose={() => setModalState(null)}
        />
      )}

      {modalState?.type === 'delete' && (
        <DeletePositionModal
          position={modalState.position}
          loading={deleteLoading}
          errorMessage={deleteError ?? undefined}
          onConfirm={handleDeleteConfirm}
          onClose={() => {
            setModalState(null);
            setDeleteError(null);
          }}
        />
      )}
    </div>
  );
};

export default PositionsTab;
